import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse, PaginatedResult } from '../../models/base';
import { BaseService } from '../base';
import {
  EditarRegistroHorasDTO,
  PausaRequest,
  RangoFechasRegistro_DTO,
  RechazarRegistroDTO,
  RechazarRegistrosPorFechasDTO,
  RegistroDetalleTiempo,
  RegistroHorasFilter,
  RegistroHorasHistorial,
  RegistroPausa,
  RegistroPausaFilter,
  ValidarRegistrosPorFechasDTO,
  RegistroHoras,
} from '../../models/personal/registro-horas.model';

const REGISTRO_HORAS_ENDPOINT = '/registrohoras/';

@Injectable({
  providedIn: 'root',
})
export class RegistroHorasService extends BaseService {
  // #region Propiedades del servicio
  loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private registrosHoras: RegistroHoras[] = [];
  rh$: BehaviorSubject<RegistroHoras[]> = new BehaviorSubject<RegistroHoras[]>([]);
  registrosHoras$: Observable<RegistroHoras[]> = this.rh$.asObservable();

  private registroPorEmpleado: PaginatedResult<RegistroDetalleTiempo> = null;
  rpe$: BehaviorSubject<PaginatedResult<RegistroDetalleTiempo>> =
    new BehaviorSubject<PaginatedResult<RegistroDetalleTiempo>>(null);
  registroPorEmpleado$: Observable<PaginatedResult<RegistroDetalleTiempo>> =
    this.rpe$.asObservable();

  private pausas: RegistroPausa[] = [];
  p$: BehaviorSubject<RegistroPausa[]> = new BehaviorSubject<RegistroPausa[]>([]);
  pausas$: Observable<RegistroPausa[]> = this.p$.asObservable();
  // #endregion

  constructor(
    private http: HttpClient,
    protected snackBar: MatSnackBar
  ) {
    super(snackBar);
  }

  // #region Métodos de consulta a la API

  /**
   * Genera un informe de horas en Excel para un empleado en un rango de fechas.
   * @param idEmployee ID del empleado
   * @param rango Rango de fechas para el informe
   * @returns Blob con el archivo Excel
   */
  informeHorasExcel(idEmployee: number, rango: RangoFechasRegistro_DTO): Observable<Blob> {
    this.loading$.next(true);

    return this.http
      .post(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'informehoras/excel/' + idEmployee,
        rango,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        map((resultado) => resultado as Blob),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Inicia un nuevo registro de horas.
   * @param registro Datos del registro a iniciar
   */
  iniciarRegistro(registro: RegistroHoras): Observable<RegistroHoras> {
    this.loading$.next(true);

    return this.http
      .post<ApiResponse<RegistroHoras>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'iniciar',
        registro
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        tap(() => {
          this.snackBar.open('Registro de horas iniciado correctamente', undefined, {
            duration: 3000,
          });
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Finaliza un registro de horas.
   * @param registro Datos del registro a finalizar
   */
  finalizarRegistro(registro: RegistroHoras): Observable<RegistroHoras> {
    this.loading$.next(true);

    return this.http
      .post<ApiResponse<RegistroHoras>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'finalizar',
        registro
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        tap(() => {
          this.snackBar.open('Registro de horas finalizado correctamente', undefined, {
            duration: 3000,
          });
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Obtiene el registro de horas no finalizado del empleado actual.
   */
  obtenerRegistroNoFinalizado(): Observable<RegistroHoras> {
    this.loading$.next(true);

    return this.http
      .get<ApiResponse<RegistroHoras>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'obtenerregistro'
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Limpia la lista de registros de horas por empleado.
   */
  limpiarListaRegistroPorEmpleado(): void {
    this.registroPorEmpleado = null;
    this.rpe$.next(this.registroPorEmpleado);
  }

  /**
   * Obtiene un listado paginado y filtrado de registros de horas.
   * @param filter Filtros a aplicar
   */
  getRegistrosHoras(filter: RegistroHorasFilter): Observable<PaginatedResult<RegistroDetalleTiempo>> {
    return this.http
      .post<ApiResponse<PaginatedResult<RegistroDetalleTiempo>>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'consultaDetalles',
        filter
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            this.registroPorEmpleado = resultado.data;
            this.rpe$.next(this.registroPorEmpleado);
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        })
      );
  }

  // #region Gestión de Pausas

  /**
   * Inicia una pausa en un registro de horas.
   * @param registroHorasId ID del registro de horas
   * @param request Datos de la pausa
   */
  iniciarPausa(registroHorasId: number, request: PausaRequest): Observable<RegistroPausa> {
    this.loading$.next(true);

    return this.http
      .post<ApiResponse<RegistroPausa>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'pausas/iniciar/' + registroHorasId,
        request
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        tap(() => {
          this.snackBar.open('Pausa iniciada correctamente', undefined, { duration: 3000 });
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Finaliza una pausa específica.
   * @param pausaId ID de la pausa
   */
  finalizarPausa(pausaId: number): Observable<RegistroPausa> {
    this.loading$.next(true);

    return this.http
      .get<ApiResponse<RegistroPausa>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'pausas/finalizar/' + pausaId
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        tap(() => {
          this.snackBar.open('Pausa finalizada correctamente', undefined, { duration: 3000 });
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Obtiene un listado filtrado de pausas.
   * @param filter Filtros a aplicar
   */
  getListaPausas(filter: RegistroPausaFilter): Observable<RegistroPausa[]> {
    this.loading$.next(true);

    return this.http
      .post<ApiResponse<RegistroPausa[]>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'consultaPausas',
        filter
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            this.pausas = resultado.data;
            this.p$.next(this.pausas);
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Obtiene todas las pausas de un registro de horas.
   * @param registroHorasId ID del registro de horas
   */
  obtenerPausas(registroHorasId: number): Observable<RegistroPausa[]> {
    this.loading$.next(true);

    return this.http
      .get<ApiResponse<RegistroPausa[]>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'pausas/' + registroHorasId
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Obtiene la pausa activa de un registro de horas, si existe.
   * @param registroHorasId ID del registro de horas
   */
  obtenerPausaActiva(registroHorasId: number): Observable<RegistroPausa> {
    this.loading$.next(true);

    return this.http
      .get<ApiResponse<RegistroPausa>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'pausas/activa/' + registroHorasId
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Valida un registro de horas.
   * @param registro Registro de horas a validar
   */
  validarRegistro(registro: RegistroDetalleTiempo): Observable<RegistroDetalleTiempo> {
    return this.http
      .get<ApiResponse<RegistroDetalleTiempo>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'validar/' + registro.registroHorasId
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        })
      );
  }

  /**
   * Valida los registros de horas pendientes para un rango de fechas.
   * @param fechaInicio Fecha de inicio del rango
   * @param fechaFin Fecha de fin del rango
   */
  validarRegistrosPendientes(
    dto: ValidarRegistrosPorFechasDTO
  ): Observable<RegistroDetalleTiempo[]> {
    return this.http
      .post<ApiResponse<RegistroDetalleTiempo[]>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'validar/entrefechas',
        dto
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        })
      );
  }

  /**
   * Edita un registro de horas ya creado. El motivo es obligatorio.
   * @param registroHorasId ID del registro
   * @param datos Campos a modificar y motivo del cambio
   */
  editarRegistro(
    registroHorasId: number,
    datos: EditarRegistroHorasDTO
  ): Observable<RegistroDetalleTiempo> {
    return this.http
      .put<ApiResponse<RegistroDetalleTiempo>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + registroHorasId,
        datos
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        tap(() => {
          this.snackBar.open('Registro editado correctamente', undefined, { duration: 3000 });
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        })
      );
  }

  /**
   * Rechaza un registro de horas. El motivo es obligatorio.
   * @param registroHorasId ID del registro
   * @param datos Motivo del rechazo
   */
  rechazarRegistro(
    registroHorasId: number,
    datos: RechazarRegistroDTO
  ): Observable<RegistroDetalleTiempo> {
    return this.http
      .post<ApiResponse<RegistroDetalleTiempo>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'rechazar/' + registroHorasId,
        datos
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        tap(() => {
          this.snackBar.open('Registro rechazado', undefined, { duration: 3000 });
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        })
      );
  }

  /**
   * Rechaza en bloque los registros pendientes de un empleado entre dos fechas.
   * @param dto Rango de fechas, empleado y motivo del rechazo
   */
  rechazarRegistrosPendientes(
    dto: RechazarRegistrosPorFechasDTO
  ): Observable<RegistroDetalleTiempo[]> {
    return this.http
      .post<ApiResponse<RegistroDetalleTiempo[]>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'rechazar/entrefechas',
        dto
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        })
      );
  }

  /**
   * Obtiene el histórico de auditoría (ediciones, rechazos, validaciones) de un registro.
   * @param registroHorasId ID del registro
   */
  obtenerHistorial(registroHorasId: number): Observable<RegistroHorasHistorial[]> {
    return this.http
      .get<ApiResponse<RegistroHorasHistorial[]>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + registroHorasId + '/historial'
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        })
      );
  }

  /**
   * Obtiene un listado paginado y filtrado de fichajes de TODOS los empleados de la empresa
   * del usuario autenticado (vista agregada).
   * @param filter Filtros a aplicar (fechas, estado, paginación)
   */
  getRegistrosHorasEmpresa(
    filter: RegistroHorasFilter
  ): Observable<PaginatedResult<RegistroDetalleTiempo>> {
    return this.http
      .post<ApiResponse<PaginatedResult<RegistroDetalleTiempo>>>(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'consulta/empresa',
        filter
      )
      .pipe(
        map((resultado) => {
          if (resultado && resultado.data) {
            return resultado.data;
          }

          this.procesarError(resultado);
          throw resultado;
        }),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        })
      );
  }

  // #region Informes (PDF / CSV / Excel de empresa)

  /**
   * Genera un informe de horas en PDF para un empleado en un rango de fechas.
   */
  informeHorasPdf(idEmployee: number, rango: RangoFechasRegistro_DTO): Observable<Blob> {
    this.loading$.next(true);

    return this.http
      .post(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'informehoras/pdf/' + idEmployee,
        rango,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        map((resultado) => resultado as Blob),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Genera un informe de horas en CSV para un empleado en un rango de fechas.
   */
  informeHorasCsv(idEmployee: number, rango: RangoFechasRegistro_DTO): Observable<Blob> {
    this.loading$.next(true);

    return this.http
      .post(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'informehoras/csv/' + idEmployee,
        rango,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        map((resultado) => resultado as Blob),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Genera el informe agregado de fichajes de toda la empresa en Excel.
   */
  informeHorasExcelEmpresa(rango: RangoFechasRegistro_DTO): Observable<Blob> {
    this.loading$.next(true);

    return this.http
      .post(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'informehoras/excel/empresa',
        rango,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        map((resultado) => resultado as Blob),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Genera el informe agregado de fichajes de toda la empresa en PDF.
   */
  informeHorasPdfEmpresa(rango: RangoFechasRegistro_DTO): Observable<Blob> {
    this.loading$.next(true);

    return this.http
      .post(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'informehoras/pdf/empresa',
        rango,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        map((resultado) => resultado as Blob),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  /**
   * Genera el informe agregado de fichajes de toda la empresa en CSV.
   */
  informeHorasCsvEmpresa(rango: RangoFechasRegistro_DTO): Observable<Blob> {
    this.loading$.next(true);

    return this.http
      .post(
        environment.apiUrl + REGISTRO_HORAS_ENDPOINT + 'informehoras/csv/empresa',
        rango,
        { responseType: 'blob' as 'json' }
      )
      .pipe(
        map((resultado) => resultado as Blob),
        catchError((error) => {
          this.procesarError(error);
          return throwError(error);
        }),
        finalize(() => {
          this.loading$.next(false);
        })
      );
  }

  // #endregion

  // #endregion
}